import { AxiosRequestConfig } from 'axios'
import hash from 'object-hash'

import { Palette, chartPalette } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  CompassAPIMultiSeriesChartData,
  getCompassAPIChartData,
  getDataSources,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import type { BaseContext } from './'
import { CommonChartConfig } from './common'

interface CategoricalChartData extends CompassAPIDataBase {
  title?: string
  data: {
    label: string
    value: number
  }[]
}

const DUPONT3_SERIES_Y_AXIS: Record<string, string> = {
  'Lucro / Receita': 'second',
  'Receita / Ativo': 'second',
  'Ativo / Patrimônio': 'default',
}

const DEFAULT_Y_AXIS = {
  id: 'default',
  opposite: false,
  title: { text: '' },
}

const PERCENTAGE_DEFAULT_Y_AXIS = {
  id: 'default',
  opposite: false,
  title: { text: '' },
  labelSuffix: '%',
}

const SECOND_Y_AXIS = {
  id: 'second',
  opposite: true,
  title: { text: '' },
  labelSuffix: '%',
}

const PERCENTAGE_URLS = [
  '/ebitda-per-rol',
  '/high-debit-percentage',
  '/expenses-higher-ebitda-percentage',
  '/negative-ebitda-percentage',
  '/yoy-rol',
  '/lucro-bruto-per-rol',
  '/financials-per-rol',
  '/lucro-liquido-per-pib',
  '/roic-and-wacc',
  '/roic-and-divida-contabil',
  '/roe-and-custo-capital',
  '/dividendos-per-pib',
  '/lucros-retidos-per-pib',
  '/payout',
  '/dividendos-per-pib',
  '/lucros-retidos-per-pib',
  '/payout',
  '/roe',
  '/roa',
  '/disponibilidade-per-rol',
]

const WITH_PROJECTION_URLS = [
  '/roic-and-wacc',
  '/divida-per-ebitda',
  '/roe-and-custo-capital',
]

const PROJECTIONS_MAP_IDS: Record<string, string> = {
  // ROIC and WACC
  roic: 'roic_projection',
  wacc_media_do_ano: 'wacc_projection',
  // Divida per EBITDA
  ratio: 'divida_per_ebitda_projection',
  // ROE and Custo capital
  roe: 'roe_projection',
  custo_de_capital_proprio: 'custo_capital_projection',
}

export namespace FinancialStatement {
  export namespace NonFinancial {
    const CONTEXT_ID = 'equity:financial_statement:non_financial'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'line' | 'bar'
      context: typeof CONTEXT_ID
      reqConfig?: Partial<AxiosRequestConfig>
    }

    export type ChartData =
      | CompassAPIMultiSeriesChartData
      | CategoricalChartData

    export const getChartData = (config: ChartConfig) =>
      getCompassAPIChartData(config, (conf) =>
        mergeDeepAll([conf, config.reqConfig ?? {}])
      )

    const getLineChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) => {
      const hasProjection = WITH_PROJECTION_URLS.some((value) =>
        config.url.endsWith(value)
      )
      const isDupont3 = config.url.endsWith('/dupont3-roe')
      const isAllPercentageSeries = PERCENTAGE_URLS.some((value) =>
        config.url.endsWith(value)
      )
      const isPercentage = (name: string) =>
        (isDupont3 && DUPONT3_SERIES_Y_AXIS[name] === 'second') ||
        isAllPercentageSeries

      const getProjectionProps = (data: CompassAPIMultiSeriesChartData) => {
        const mainSeriesIds = Object.keys(PROJECTIONS_MAP_IDS)
        const mainSeries = data.data.filter((s) =>
          mainSeriesIds.includes(s.id ?? '')
        )
        const colorMap: Record<string, string> = {}
        const linkedToMap: Record<string, string> = {}
        const showInLegendMap: Record<string, boolean> = {}
        mainSeries.forEach((s, idx) => {
          const id = s.id ?? ''
          const projectionName = PROJECTIONS_MAP_IDS[id]
          colorMap[id] = chartPalette[idx]
          colorMap[projectionName] = chartPalette[idx]
          linkedToMap[projectionName] = id
          showInLegendMap[id] = true
          showInLegendMap[projectionName] = false
        })
        return {
          dashStyle: data.data.map((s) =>
            mainSeriesIds.includes(s.id ?? '') ? 'Solid' : 'ShortDash'
          ),
          ids: data.data.map((s, idx) => s.id ?? `series-${idx}`),
          color: data.data.map((s) => (s.id ? colorMap[s.id] : Palette.gray)),
          linkedTo: data.data.map((s) =>
            s.id ? linkedToMap[s.id] : undefined
          ),
          showInLegend: data.data.map((s) =>
            s.id ? showInLegendMap[s.id] : false
          ),
        }
      }

      return getMultipleLineChartProps(
        Context,
        {
          convertToPercentages: data.data.map((item) =>
            isPercentage(item.name)
          ),
          addMinMaxIndicators: true,
          xAxis: { labels: { format: '{value:%Y}' } },
          yAxis: [
            isAllPercentageSeries ? PERCENTAGE_DEFAULT_Y_AXIS : DEFAULT_Y_AXIS,
            ...(isDupont3 ? [SECOND_Y_AXIS] : []),
          ],
        },
        {
          ...(hasProjection ? getProjectionProps(data) : {}),
          type: Array(data.data.length).fill('spline'),
          yAxis: data.data.map((item) =>
            isDupont3 ? DUPONT3_SERIES_Y_AXIS[item.name] : 'default'
          ),
          tooltip: data.data.map((item) => ({
            pointFormatter: magnitudePointFormatter({
              unit: isPercentage(item.name) ? '%' : '',
            }),
            xDateFormat: '%B %Y',
          })),
        }
      )(config, data)
    }

    const getBarChartProps = (
      config: ChartConfig,
      data: CategoricalChartData
    ) => {
      const title = config.title ?? data.title
      const series = [
        {
          data: data.data?.map((info) => [info.label, info.value * 100]),
          id: title,
          marker: { enabled: false },
          name: title,
          type: 'column',
          yAxis: 'default',
          tooltip: { valueDecimals: 2, valueSuffix: '%' },
          colors: data.data?.map((info) =>
            info.label.toLowerCase() === 'total'
              ? '#000000'
              : info.value >= 0
              ? '#0000ff'
              : '#ff0000'
          ),
        },
      ]

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          labels: { format: '{value}%' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title,
        options: mergeDeepAll([
          creditsConfig,
          {
            plotOptions: { column: { stacking: 'normal', colorByPoint: true } },
            tooltip: { shared: true },
            xAxis: { type: 'category' },
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'line':
            return getLineChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'bar':
            return getBarChartProps(config, data as CategoricalChartData)
        }
      },
    }
  }
}
